import React, { useMemo, useState } from "react";
import { useTable, useSortBy, useGlobalFilter } from "react-table";
import ColumnActions from "./ColumnActions";

// Global filter component
const GlobalFilter = ({ globalFilter, setGlobalFilter }) => {
  return (
    <div className="mb-4">
      <input
        value={globalFilter || ""}
        onChange={(e) => setGlobalFilter(e.target.value)}
        placeholder="Search all columns..."
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </div>
  );
};

const DataTable = ({ data, title, maxHeight = "400px", headerInfo, expectedHeaders }) => {
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationType, setNotificationType] = useState(""); // "success" or "error"

  // Notification handler for copy operations
  const handleNotification = (message, type = "success") => {
    setNotificationMessage(message);
    setNotificationType(type);

    // Auto-clear notification after 5 seconds
    setTimeout(() => {
      setNotificationMessage("");
      setNotificationType("");
    }, 5000);
  };
  // Memoize columns for better performance
  const columns = useMemo(() => {
    // If this is the main room allocation data, use specific columns.
    // Labels come from Settings > Column Headers (expectedHeaders is ordered A..L).
    if (expectedHeaders && expectedHeaders.length > 0) {
      const label = (index, fallback) => expectedHeaders[index] || fallback;

      return [
        {
          Header: label(0, "Item No"),
          accessor: "item_no",
          Cell: ({ value }) => value || "",
        },
        {
          Header: label(1, "Short Code"),
          id: "short_code",
          accessor: (row) => row.short_code || row.unit_no,
          Cell: ({ value }) => value || "",
        },
        {
          Header: label(2, "Class Name"),
          id: "class_name",
          accessor: (row) => row.class_name || row.vle_class_name,
          Cell: ({ value }) => value || "",
        },
        {
          Header: label(3, "Unit Name"),
          accessor: "unit_name",
          Cell: ({ value }) => value || "",
        },
        {
          Header: label(4, "Tutor Name"),
          accessor: "tutor_name",
          Cell: ({ value }) => value || "",
        },
        {
          Header: label(5, "Day/Time"),
          accessor: "day_time",
          Cell: ({ value }) => value || "",
        },
        {
          Header: label(6, "EW/Time"),
          accessor: "ew_time",
          Cell: ({ value }) => value || "",
        },
        {
          Header: label(7, "Group"),
          accessor: "group",
          Cell: ({ value }) => value || "",
        },
        {
          Header: label(8, "Cohort"),
          id: "cohort",
          accessor: (row) => row.cohort || row.intake,
          Cell: ({ value }) => value || "",
        },
        {
          Header: () => (
            <div className="flex items-center justify-between group">
              <span>{label(9, "Room Allocation")}</span>
              <svg
                className="h-4 w-4 text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                title="This column can be copied using the Copy Room Allocation button above"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 5H6a2 2 0 00-2 2v6a2 2 0 002 2h2m2-6a2 2 0 012-2h2a2 2 0 012 2v6a2 2 0 01-2 2h-2m-6 0h4"
                />
              </svg>
            </div>
          ),
          // Plain-text label for code that can't read the JSX header (e.g. copy actions)
          headerLabel: label(9, "Room Allocation"),
          id: "room_allocation",
          accessor: (row) => row.room_allocation || row.room_auto_allo,
          Cell: ({ value, row }) => {
            // Visual indicator for recently updated room allocations
            const isRecentlyUpdated = row.original.recently_updated_room;

            return (
              <span
                className={
                  isRecentlyUpdated
                    ? "bg-green-200 px-2 py-1 rounded font-semibold animate-pulse"
                    : ""
                }
                title={isRecentlyUpdated ? "Recently updated from conflict resolution" : ""}
              >
                {value || ""}
              </span>
            );
          },
        },
        {
          Header: label(10, "Students Coming"),
          accessor: "students_coming",
          Cell: ({ value }) => value || "",
        },
        {
          Header: label(11, "Student Number"),
          id: "student_number",
          accessor: (row) => row.student_number || row.student_numbers,
          Cell: ({ value }) => value || "",
        },
      ];
    }

    // For other tables (conflicts, unallocated), generate columns dynamically
    if (!data || data.length === 0) return [];

    const keys = Object.keys(data[0]);
    return keys.map((key) => ({
      Header: key.replace(/_/g, " ").toUpperCase(),
      id: key,
      accessor: key,
      Cell: ({ value }) => {
        if (value === null || value === undefined) return "";
        if (typeof value === "object") return JSON.stringify(value);
        return String(value);
      },
    }));
  }, [data, expectedHeaders]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data: data || [],
    },
    useGlobalFilter,
    useSortBy
  );

  const { globalFilter } = state;

  if (!data || data.length === 0) {
    return <div className="text-center py-8 text-gray-500">No data available</div>;
  }

  return (
    <div className="w-full">
      {/* Display captions above the table if headerInfo is provided */}
      {headerInfo && (headerInfo.session || headerInfo.department) && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="text-lg font-semibold text-blue-900">
            📚 {headerInfo.session}
            {headerInfo.session && headerInfo.department ? " , " : ""}
            {headerInfo.department}
          </div>
        </div>
      )}

      {/* Notification Banner */}
      {notificationMessage && (
        <div
          className={`mb-4 p-3 rounded-lg border-l-4 ${
            notificationType === "success"
              ? "bg-green-50 border-green-400 text-green-700"
              : "bg-red-50 border-red-400 text-red-700"
          }`}
        >
          <div className="flex items-center">
            {notificationType === "success" ? (
              <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            ) : (
              <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            )}
            <span className="text-sm font-medium">{notificationMessage}</span>
          </div>
        </div>
      )}

      {/* Column Actions Component - Professional Copy Features */}
      <ColumnActions
        data={data}
        columns={columns}
        tableName={title || "Table"}
        onNotification={handleNotification}
      />

      <GlobalFilter globalFilter={globalFilter} setGlobalFilter={setGlobalFilter} />

      <div className="table-container" style={{ maxHeight }}>
        <table {...getTableProps()} className="table">
          <thead>
            {headerGroups.map((headerGroup) => {
              const { key: headerKey, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
              return (
                <tr key={headerKey} {...headerGroupProps}>
                  {headerGroup.headers.map((column) => {
                    const { key: columnKey, ...columnProps } = column.getHeaderProps(
                      column.getSortByToggleProps()
                    );
                    return (
                      <th
                        key={columnKey}
                        {...columnProps}
                        className="bg-gray-50 px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center space-x-1">
                          <span>{column.render("Header")}</span>
                          <span className="text-gray-400">
                            {column.isSorted ? (column.isSortedDesc ? " ↓" : " ↑") : " ↕"}
                          </span>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row);
              const isSection = row.original.is_section_header;

              if (isSection) {
                // Render section header row
                return (
                  <tr key={row.id} className="bg-blue-50">
                    <td
                      colSpan={columns.length}
                      className="px-3 py-3 text-left font-semibold text-blue-900 border-b border-blue-200"
                    >
                      {row.original.section_title}
                    </td>
                  </tr>
                );
              }

              // Render regular data row
              const { key: rowKey, ...rowProps } = row.getRowProps();
              return (
                <tr key={rowKey} {...rowProps} className="hover:bg-gray-50">
                  {row.cells.map((cell) => {
                    const { key: cellKey, ...cellProps } = cell.getCellProps();
                    return (
                      <td
                        key={cellKey}
                        {...cellProps}
                        className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 border-b border-gray-200"
                      >
                        {cell.render("Cell")}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-2 text-sm text-gray-500">
        Showing {rows.length} of {data.length} rows
      </div>
    </div>
  );
};

export default DataTable;
